import TablePagination from "@mui/material/TablePagination";
import TableSortLabel from "@mui/material/TableSortLabel";
import TableContainer from "@mui/material/TableContainer";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { visuallyHidden } from "@mui/utils";
import Table from "@mui/material/Table";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import REACT_APP from "../../../environment";
import Loading from "../../Loading";
import PropTypes from "prop-types";
import Error from "../../Error";
import * as React from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@mui/material";
import { useTranslation } from "react-i18next";

function descendingComparator(a, b, orderBy) {
    if (b[orderBy] < a[orderBy]) return -1;
    if (b[orderBy] > a[orderBy]) return 1;
    return 0;
}

function getComparator(order, orderBy) {
    return order === "desc" ? (a, b) => descendingComparator(a, b, orderBy) : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
        const order = comparator(a[0], b[0]);
        if (order !== 0) return order;
        return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
}

const headCells = [
    { id: "company_tax", numeric: true, label: "Company Tax" },
    { id: "employee_tax", numeric: true, label: "Employee Tax" },
    { id: "total_amount", numeric: true, label: "Total Amount" },
    { id: "net_amount", numeric: true, label: "Net Amount" },
    { id: "amount", numeric: true, label: "Amount" },
    { id: "begin_date", numeric: true, label: "Begin Date" },
    { id: "end_date", numeric: true, label: "End Date" },
];

function EnhancedTableHead(props) {
    const { t } = useTranslation();
    const { order, orderBy, onRequestSort } = props;
    const createSortHandler = (property) => (event) => onRequestSort(event, property);

    return (
        <TableHead>
            <TableRow>
                {headCells.map((headCell) => (
                    <TableCell key={headCell.id} align="left" sortDirection={orderBy === headCell.id ? order : false}>
                        <TableSortLabel
                            active={orderBy === headCell.id}
                            direction={orderBy === headCell.id ? order : "asc"}
                            onClick={createSortHandler(headCell.id)}
                        >
                            {t(headCell.label)}
                            {orderBy === headCell.id ? (
                                <Box component="span" sx={visuallyHidden}>
                                    {order === "desc" ? "sorted descending" : "sorted ascending"}
                                </Box>
                            ) : null}
                        </TableSortLabel>
                    </TableCell>
                ))}
            </TableRow>
        </TableHead>
    );
}

EnhancedTableHead.propTypes = {
    orderBy: PropTypes.string.isRequired,
    rowCount: PropTypes.number.isRequired,
};

function PayrollTable() {
    const { t } = useTranslation();
    const params = useParams();
    const [page, setPage] = React.useState(0);
    const [data, setData] = React.useState([]);
    const [order, setOrder] = React.useState("asc");
    const [loading, setLoading] = React.useState(true);
    const [errorMsg, setErrorMsg] = React.useState(null);
    const [rowsPerPage, setRowsPerPage] = React.useState(5);
    const [orderBy, setOrderBy] = React.useState("calories");

    const getData = async () => {
        setErrorMsg(null);
        setLoading(true);
        axios(`${REACT_APP.URL}/api/hrp/v1/payroll/employee/payroll/${params.id}/history`, {
            headers: { Authorization: sessionStorage.getItem("token") },
        })
            .then((res) => {
                setData(res.data.data);
                setLoading(false);
            })
            .catch((err) => {
                setErrorMsg(err.message);
                setLoading(false);
            });
    };

    const handleRequestSort = (event, property) => {
        const isAsc = orderBy === property && order === "asc";
        setOrder(isAsc ? "desc" : "asc");
        setOrderBy(property);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const navigate = useNavigate();
    const onBack = () => navigate("/hr/salary");

    React.useEffect(() => {
        getData();
    }, []);

    return (
        <div className="py-4 px-2">
            <Button className="mb-3" variant="contained" color="primary" onClick={onBack}>
                {t("Back")}
            </Button>
            {loading ? (
                <Loading />
            ) : errorMsg ? (
                <Error message={errorMsg} />
            ) : (
                <div>
                    <div className="d-flex justify-content-between row pb-3">
                        <div className="col-sm-4">
                            <h4 className="text-center text-sm-start">{t("Payroll")}</h4>
                        </div>
                    </div>
                    <Box sx={{ width: "100%" }}>
                        <Paper sx={{ width: "100%", mb: 2 }}>
                            <TableContainer>
                                <Table sx={{ minWidth: 750, px: 2 }} aria-labelledby="tableTitle" size="medium">
                                    <EnhancedTableHead order={order} onRequestSort={handleRequestSort} orderBy={orderBy} rowCount={data.length} />
                                    <TableBody>
                                        {stableSort(data, getComparator(order, orderBy))
                                            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                            .map((row, index) => {
                                                return (
                                                    <TableRow hover tabIndex={-1} key={row.id}>
                                                        <TableCell align="left">{row.company_tax}</TableCell>
                                                        <TableCell align="left">{row.employee_tax}</TableCell>
                                                        <TableCell align="left">{row.total_amount}</TableCell>
                                                        <TableCell align="left">{row.net_amount}</TableCell>
                                                        <TableCell align="left">{row.amount}</TableCell>
                                                        <TableCell align="left">{row.begin_date?.slice(0, 10)}</TableCell>
                                                        <TableCell align="left">{row.end_date?.slice(0, 10)}</TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <TablePagination
                                component="div"
                                labelRowsPerPage={t("Rows per page")}
                                rowsPerPageOptions={[5, 10, 25, 100]}
                                count={data.length}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                            />
                        </Paper>
                    </Box>
                </div>
            )}
        </div>
    );
}

export default React.memo(PayrollTable);
